import { useEffect, useRef, useCallback, useMemo, useSyncExternalStore } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { cookieAuthEnabled } from '@/services/cookieAuthFlag'
import { registerClientStateReset } from '@/lib/queryClient'

export type EventType =
  | 'execution.created'
  | 'execution.queued'
  | 'execution.started'
  | 'execution.completed'
  | 'execution.failed'
  | 'execution.cancelled'
  | 'execution.log'
  | 'workflow.created'
  | 'workflow.updated'
  | 'workflow.deleted'
  | 'workflow.activated'
  | 'budget.alert'
  | 'budget.exceeded'
  | 'quota.warning'
  | 'notification.created'
  | 'activity.created'
  | 'approval.created'
  | 'approval.decided'
  // Declared-gate follow-up (ADR-130, approvals-ui-declared-gates): emitted
  // when a precreated "declared" gate card (see ApprovalDecidability in
  // features/approvals/types.ts) becomes decidable — the agent reported the
  // exact tool call. Not yet emitted by every backend revision; consumers
  // must tolerate it never arriving (a periodic list refetch reaches the
  // same state eventually via decidability flipping on the fetched row).
  | 'approval.call_reported'
  | 'ping'
  | 'pong'

export interface WebSocketMessage<T = unknown> {
  type: EventType
  tenant_id: string
  timestamp: string
  data: T
}

export interface ExecutionEventData {
  execution_id: string
  workflow_id: string
  workflow_name: string
  status: string
  previous_status?: string
  error?: string
  progress?: number
  duration_ms?: number
}

export interface ExecutionLogEventData {
  execution_id: string
  level: string
  message: string
  node_id?: string
  node_type?: string
  timestamp: string
}

export interface WorkflowEventData {
  workflow_id: string
  name: string
  status: string
  version_id?: string
}

// NOTE: this is the real-time WebSocket push shape (backend
// internal/websocket/types.go), distinct by design from the REST BudgetAlert in
// features/cost/types.ts. Field naming differs: WS uses `current_spend_cents`
// while REST uses `current_amount_cents`. Do not assume they are interchangeable
// — populating the REST alerts cache from a WS event requires field mapping.
export interface BudgetAlertEventData {
  budget_id: string
  budget_name: string
  threshold_percent: number
  current_spend_cents: number
  budget_amount_cents: number
  percent_used: number
}

export interface ActivityEventData {
  activity_id: string
  workflow_id: string
  workflow_name?: string
  user_id: string
  user_name?: string
  type: string
  description: string
  data?: Record<string, unknown>
  created_at: string
}

type MessageHandler = (message: WebSocketMessage) => void

interface UseWebSocketOptions {
  autoConnect?: boolean
  reconnectInterval?: number
  maxReconnectAttempts?: number
  channels?: string[]
}

interface UseWebSocketReturn {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  connect: () => void
  disconnect: () => void
  subscribe: (channels: string[]) => void
  unsubscribe: (channels: string[]) => void
  addHandler: (handler: MessageHandler) => () => void
}

// Derive the WebSocket base URL.
// Priority: explicit VITE_WS_URL build-time env var → auto-derive from page origin.
// Auto-derive converts https://example.com → wss://example.com/api/v1/ws
// (the frontend nginx at /api/ proxies to the api-gateway which serves /ws there).
// This makes WebSocket work without any build-time configuration while remaining
// correct when an explicit override is needed (e.g. cross-origin deployments).
function deriveWsUrl(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL as string
  }
  if (typeof window === 'undefined') {
    return ''
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/api/v1/ws`
}

// Resolved lazily + memoized on first use (NOT at module-load) so that SSR and
// JSDOM/unit-test imports don't eagerly resolve a ws:// URL or trigger connects.
let _wsUrlMemo: string | undefined
function getWsUrl(): string {
  if (_wsUrlMemo === undefined) {
    _wsUrlMemo = deriveWsUrl()
  }
  return _wsUrlMemo
}
function wsEnabled(): boolean {
  return getWsUrl() !== ''
}

// Singleton WebSocket manager - shared across all hook instances
class WebSocketManager {
  private ws: WebSocket | null = null
  private handlers = new Set<MessageHandler>()
  private subscribers = new Set<object>() // Track hook instances using the connection
  private reconnectAttempts = 0
  private reconnectTimeout: number | null = null
  private _isConnected = false
  private _isConnecting = false
  private _error: string | null = null
  private token: string | null = null
  private listeners = new Set<() => void>()
  // Cached snapshot for useSyncExternalStore - must be referentially stable
  private _snapshot: { isConnected: boolean; isConnecting: boolean; error: string | null }

  private maxReconnectAttempts = 5
  private reconnectInterval = 3000

  constructor() {
    // Initialize snapshot
    this._snapshot = { isConnected: false, isConnecting: false, error: null }
  }

  get isConnected() { return this._isConnected }
  get isConnecting() { return this._isConnecting }
  get error() { return this._error }

  private notify() {
    // Update cached snapshot only when values change
    this._snapshot = { isConnected: this._isConnected, isConnecting: this._isConnecting, error: this._error }
    this.listeners.forEach(listener => listener())
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot() {
    // Return cached snapshot for referential stability (required by useSyncExternalStore)
    return this._snapshot
  }

  addSubscriber(subscriber: object) {
    this.subscribers.add(subscriber)
  }

  removeSubscriber(subscriber: object) {
    this.subscribers.delete(subscriber)
    // Only disconnect if no more subscribers
    if (this.subscribers.size === 0) {
      this.disconnect()
    }
  }

  connect(token: string | null) {
    // Resolve the WS URL lazily here (first connect), not at module load.
    const base = getWsUrl()
    if (!base) {
      return
    }

    // Store token for reconnection (may be null in WS-2 cookie-only mode)
    this.token = token

    // Don't reconnect if already connected or connecting
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return
    }

    this._isConnecting = true
    this._error = null
    this.notify()

    try {
      // Pass the token as a query parameter on the upgrade URL.
      // Browsers cannot set an Authorization header on WebSocket upgrade requests,
      // so the backend auth middleware is configured to read ?token=<jwt>.
      // The token is already short-lived and the connection switches to wss (TLS),
      // so the exposure surface is equivalent to a Bearer header on HTTP.
      //
      // WS-2 cookie-only mode: with no token, omit ?token= entirely and rely on
      // the HttpOnly session cookie carried on the same-origin upgrade handshake.
      // The gateway's extractClaims checks the cookie branches BEFORE the ?token=
      // query branch, so the WS authenticates via the cookie. (Inert on a DevMode
      // cluster, which resolves identity from the header/query, not the cookie.)
      const wsUrl = token ? `${base}?token=${encodeURIComponent(token)}` : base
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        this._isConnected = true
        this._isConnecting = false
        this._error = null
        this.reconnectAttempts = 0
        this.notify()
      }

      ws.onmessage = (event) => {
        const dispatch = (message: WebSocketMessage) => {
          this.handlers.forEach((handler) => {
            try {
              handler(message)
            } catch (err) {
              console.error('WebSocket handler error:', err)
            }
          })
        }
        try {
          dispatch(JSON.parse(event.data))
        } catch {
          // Older servers batched several queued events into one frame joined
          // by "\n". Fall back to parsing each line on its own.
          const lines = String(event.data).split('\n')
          for (const line of lines) {
            if (line.trim() === '') continue
            let message: WebSocketMessage
            try {
              message = JSON.parse(line)
            } catch (err) {
              console.error('Failed to parse WebSocket message:', err)
              continue
            }
            dispatch(message)
          }
        }
      }

      ws.onerror = () => {
        this._error = 'WebSocket connection error'
        this.notify()
      }

      ws.onclose = (event) => {
        // A superseded/disconnected socket must not touch the current one's
        // state or schedule a reconnect with a stale token (#301).
        if (ws !== this.ws) return
        this._isConnected = false
        this._isConnecting = false
        this.ws = null
        this.notify()

        // Only reconnect if we still have subscribers and it wasn't a clean close
        if (this.subscribers.size > 0 && event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts && this.token) {
          this.reconnectAttempts++
          this.reconnectTimeout = window.setTimeout(() => {
            if (this.token) {
              this.connect(this.token)
            }
          }, this.reconnectInterval)
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          this._error = 'Max reconnection attempts reached'
          this.notify()
        }
      }

      this.ws = ws
    } catch {
      this._isConnecting = false
      this._error = 'Failed to create WebSocket connection'
      this.notify()
    }
  }

  disconnect() {
    if (this.reconnectTimeout) {
      window.clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    if (this.ws) {
      // Detach handlers first: nothing from the old identity's socket may
      // reach subscribers once we have decided to drop it (#301).
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onerror = null
      this.ws.onclose = null
      this.ws.close(1000) // Normal closure
      this.ws = null
    }
    this._isConnected = false
    this._isConnecting = false
    this.token = null
    this.notify()
  }

  sendMessage(channels: string[], action: 'subscribe' | 'unsubscribe') {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action, channels }))
    }
  }

  addHandler(handler: MessageHandler) {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }
}

// Single global instance
const wsManager = new WebSocketManager()

// The socket was opened with the previous tenant's token (?token=). Close it on
// any identity/tenant change; the hook's auto-connect effect reconnects with the
// new token while subscribers remain mounted (issue #301).
registerClientStateReset(() => wsManager.disconnect(), 'websocket')

// Empty array constant to avoid creating new references
const EMPTY_CHANNELS: string[] = []

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    autoConnect = false,
    channels: channelsProp,
  } = options

  // Use stable reference for channels to prevent infinite re-renders
  const channels = channelsProp ?? EMPTY_CHANNELS
  const channelsKey = channels.join(',')

  const token = useAuthStore((state) => state.token)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const subscriberRef = useRef({})
  // WS-2: in cookie-only mode there is no JS-readable token — connect on the
  // authenticated session and let the HttpOnly cookie carry the WS handshake.
  const canConnect = !!token || (cookieAuthEnabled() && isAuthenticated)

  // Use useSyncExternalStore for reactive state from the singleton manager
  const state = useSyncExternalStore(
    (listener) => wsManager.subscribe(listener),
    () => wsManager.getSnapshot()
  )

  // Register this hook instance as a subscriber
  useEffect(() => {
    const subscriber = subscriberRef.current
    wsManager.addSubscriber(subscriber)
    return () => {
      wsManager.removeSubscriber(subscriber)
    }
  }, [])

  // Auto-connect when authenticated (token in Bearer mode, or cookie session in
  // cookie-only mode). connect() omits ?token= when token is null.
  useEffect(() => {
    if (autoConnect && canConnect && !state.isConnected && !state.isConnecting) {
      wsManager.connect(token)
    }
  }, [autoConnect, canConnect, token, state.isConnected, state.isConnecting])

  // Subscribe to initial channels when connected
  // Use channelsKey (string) as dependency instead of channels array to avoid reference issues
  useEffect(() => {
    if (state.isConnected && channels.length > 0) {
      wsManager.sendMessage(channels, 'subscribe')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected, channelsKey])

  const connect = useCallback(() => {
    if (canConnect) {
      wsManager.connect(token)
    }
  }, [canConnect, token])

  const disconnect = useCallback(() => {
    wsManager.disconnect()
  }, [])

  const subscribe = useCallback((newChannels: string[]) => {
    wsManager.sendMessage(newChannels, 'subscribe')
  }, [])

  const unsubscribe = useCallback((channelsToRemove: string[]) => {
    wsManager.sendMessage(channelsToRemove, 'unsubscribe')
  }, [])

  const addHandler = useCallback((handler: MessageHandler) => {
    return wsManager.addHandler(handler)
  }, [])

  return {
    isConnected: state.isConnected,
    isConnecting: state.isConnecting,
    error: state.error,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    addHandler,
  }
}

// Whether WebSocket is configured/enabled (lazily resolves the URL on first call).
// Exported so feature hooks outside this file (e.g.
// features/chat/hooks/useApprovalAutoResume.ts) can gate their own
// autoConnect/subscribe decisions on the same signal every hook in this
// file already uses below, instead of re-deriving it (see
// components/ui/ConnectionStatus.tsx's OWN simplified re-derivation, which
// this avoids duplicating further).
export const wsConfigured = wsEnabled()

// Hook for execution-specific events.
// Returns { isConnected } so callers (e.g. useExecution in
// features/executions/hooks/useExecutions.ts) can use the live WS connection
// state to decide their own polling/caching strategy — WS push is the
// primary update path while connected, with polling as a safety net only.
export function useExecutionEvents(
  executionId?: string,
  onStatusChange?: (data: ExecutionEventData) => void,
  onLog?: (data: ExecutionLogEventData) => void
): { isConnected: boolean } {
  // Memoize channels to prevent infinite re-renders
  const channels = useMemo(
    () => (executionId ? [`executions:${executionId}`] : ['executions']),
    [executionId]
  )

  const { addHandler, subscribe, unsubscribe, isConnected } = useWebSocket({
    autoConnect: wsConfigured,
    channels,
  })

  useEffect(() => {
    if (!isConnected) return

    const removeHandler = addHandler((message) => {
      if (message.type.startsWith('execution.')) {
        if (message.type === 'execution.log' && onLog) {
          const data = message.data as ExecutionLogEventData
          if (!executionId || data.execution_id === executionId) {
            onLog(data)
          }
        } else if (onStatusChange) {
          const data = message.data as ExecutionEventData
          if (!executionId || data.execution_id === executionId) {
            onStatusChange(data)
          }
        }
      }
    })

    // Subscribe to specific execution if ID provided
    if (executionId) {
      subscribe([`executions:${executionId}`])
    }

    return () => {
      removeHandler()
      if (executionId) {
        unsubscribe([`executions:${executionId}`])
      }
    }
  }, [executionId, onStatusChange, onLog, addHandler, subscribe, unsubscribe, isConnected])

  return { isConnected }
}

// Hook for workflow events
export function useWorkflowEvents(
  workflowId?: string,
  onChange?: (data: WorkflowEventData, eventType: EventType) => void
) {
  // Memoize channels to prevent infinite re-renders
  const channels = useMemo(
    () => (workflowId ? [`workflows:${workflowId}`] : ['workflows']),
    [workflowId]
  )

  const { addHandler, subscribe, unsubscribe, isConnected } = useWebSocket({
    autoConnect: wsConfigured,
    channels,
  })

  useEffect(() => {
    if (!isConnected) return

    const removeHandler = addHandler((message) => {
      if (message.type.startsWith('workflow.') && onChange) {
        const data = message.data as WorkflowEventData
        if (!workflowId || data.workflow_id === workflowId) {
          onChange(data, message.type)
        }
      }
    })

    if (workflowId) {
      subscribe([`workflows:${workflowId}`])
    }

    return () => {
      removeHandler()
      if (workflowId) {
        unsubscribe([`workflows:${workflowId}`])
      }
    }
  }, [workflowId, onChange, addHandler, subscribe, unsubscribe, isConnected])
}

// Stable channel references for hooks
const BUDGETS_CHANNELS = ['budgets']

// Hook for budget alerts
export function useBudgetAlerts(
  onAlert?: (data: BudgetAlertEventData) => void
) {
  const { addHandler, isConnected } = useWebSocket({
    autoConnect: wsConfigured,
    channels: BUDGETS_CHANNELS,
  })

  useEffect(() => {
    if (!isConnected || !onAlert) return

    const removeHandler = addHandler((message) => {
      if (message.type === 'budget.alert' || message.type === 'budget.exceeded') {
        onAlert(message.data as BudgetAlertEventData)
      }
    })

    return removeHandler
  }, [onAlert, addHandler, isConnected])
}

// Hook for activity events (real-time activity feed updates)
export function useActivityEvents(
  workflowId?: string,
  onActivity?: (data: ActivityEventData) => void
) {
  // Memoize channels to prevent infinite re-renders
  const channels = useMemo(
    () => (workflowId ? [`activities:${workflowId}`] : ['activities']),
    [workflowId]
  )

  const { addHandler, subscribe, unsubscribe, isConnected } = useWebSocket({
    autoConnect: wsConfigured,
    channels,
  })

  useEffect(() => {
    if (!isConnected) return

    const removeHandler = addHandler((message) => {
      if (message.type === 'activity.created' && onActivity) {
        const data = message.data as ActivityEventData
        if (!workflowId || data.workflow_id === workflowId) {
          onActivity(data)
        }
      }
    })

    // Subscribe to specific workflow activities if ID provided
    if (workflowId) {
      subscribe([`activities:${workflowId}`])
    }

    return () => {
      removeHandler()
      if (workflowId) {
        unsubscribe([`activities:${workflowId}`])
      }
    }
  }, [workflowId, onActivity, addHandler, subscribe, unsubscribe, isConnected])
}
